import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  message,
  Popconfirm,
  Table,
  Tooltip,
  Drawer,
  InputNumber,
  PageHeader,
} from "antd";
import { createComponentStore, useStoreState } from "easy-peasy";
import React, { useEffect, useState } from "react";
import {
  ParameterStoreModel,
  ACTION_TYPES,
} from "store/models/parameters/general-parameter";
import DatePicker from "Components/date-picker-localized";
import moment from "moment";
import { fixDates } from "Utils/";
import EditableCell from "../../general/components/editable-cell";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import Yup from "ValidationModels/localizedYup";
import { yupResolver } from "@hookform/resolvers";
import { useForm } from "react-hook-form";

export default function ConditionTypeInterestRates({
  title,
  api,
  parentId,
  onRefreshList,
  ...props
}) {
  const { abilities } = useStoreState((store) => store.auth);

  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);

  const isEditing = (record) => record.id === selectedRow?.id;

  const useParameterStore = createComponentStore(ParameterStoreModel(api));
  const [store, actions] = useParameterStore();

  const { fetchSingle, update, create, remove } = actions;

  useEffect(() => {
    fetchSingle({ id: parentId });
  }, []);

  useEffect(() => {
    if (store.process === ACTION_TYPES.FETCHED_DATA) {
      setTableData(fixDates(store.record.interests));
    }

    if (store.process === ACTION_TYPES.CREATED_DATA) {
      message.success(`Faiz oranı eklendi.`);
      fetchSingle({ id: parentId });
      onRefreshList();
    }

    if (store.process === ACTION_TYPES.UPDATED_DATA) {
      message.success(`Faiz oranı güncellendi.`);
      fetchSingle({ id: parentId });
      onRefreshList();
    }

    if (store.process === ACTION_TYPES.REMOVED_DATA) {
      message.success(`Faiz oranı silindi.`);
      fetchSingle({ id: parentId });
      onRefreshList();
    }
  }, [store.process]);

  const columns = [
    {
      title: "Geçerlilik Başlangıç Tarihi",
      dataIndex: "dateOfBeginning",
      key: "dateOfBeginning",
      editable: true,
      inputNode: <DatePicker format="DD.MM.YYYY" />,
      render: (text, record) =>
        moment(record.dateOfBeginning).format("DD.MM.YYYY"),
    },
    {
      title: "Geçerlilik Bitiş Tarihi",
      dataIndex: "dateOfFinish",
      key: "dateOfFinish",
      editable: true,
      inputNode: <DatePicker format="DD.MM.YYYY" />,
      render: (text, record) =>
        moment(record.dateOfFinish).format("DD.MM.YYYY"),
    },
    {
      title: "Yıllık Oran (%)",
      dataIndex: "percentage",
      key: "percentage",
      editable: true,
      inputNode: <InputNumber />,
      render: (text, record) =>
        record.percentage?.toFixed(2).replace(".", ",") || "",
    },
    {
      title: "İşlemler",
      width: "150px",
      dataIndex: "operation",
      render: (text, record) => {
        return isEditing(record) ? (
          <span>
            <Tooltip title="Sakla">
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  store.process === ACTION_TYPES.CREATING_DATA ||
                  store.process === ACTION_TYPES.UPDATING_DATA
                }
                icon={<SaveOutlined />}
                style={{
                  marginRight: 8,
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Emin Misin?"
              onConfirm={() => handleCancel(record.id)}
              icon={<RollbackOutlined />}
              okText="İptal"
              cancelText="Geri"
            >
              <Tooltip title="İptal" placement="bottom">
                <Button type="default" icon={<RollbackOutlined />} style={{}} />
              </Tooltip>
            </Popconfirm>
          </span>
        ) : (
          <span>
            <Tooltip title="Düzenle">
              <Button
                type="primary"
                icon={<EditOutlined />}
                disabled={
                  AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Koşul Tipleri")
                    ? selectedRow
                      ? true
                      : false
                    : true
                }
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedRow(record);
                }}
                style={{
                  marginRight: 8,
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Emin Misin?"
              onConfirm={() => handleDelete(record)}
              icon={<DeleteOutlined />}
              okText="Kaldır"
              cancelText="Geri"
            >
              <Tooltip title="Kaldır" placement="bottom">
                <Button
                  disabled={
                    AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Koşul Tipleri")
                      ? selectedRow
                        ? true
                        : record.id === "new"
                      : true
                  }
                  loading={store.process === ACTION_TYPES.REMOVING_DATA}
                  type="danger"
                  icon={<DeleteOutlined />}
                  style={{
                    marginRight: 8,
                  }}
                />
              </Tooltip>
            </Popconfirm>
          </span>
        );
      },
    },
  ];

  const FormValidation = Yup.object({
    dateOfBeginning: Yup.date().required(),
    dateOfFinish: Yup.date().required(),
    percentage: Yup.number().required(),
  });

  const form = useForm({
    mode: "onSubmit",
    resolver: yupResolver(FormValidation),
    defaultValues: {},
  });

  const { handleSubmit, errors, reset } = form;

  const formOnSubmit = async (form) => {
    let data = { ...store.record };
    if (selectedRow.id !== "new") {
      data.interests = data.interests.filter(
        (item) => item.id !== selectedRow.id
      );
    }
    data.interests.push(form);

    await update(data);

    handleCancel();
  };

  const handleTablePagination = (change) => {
    setCurrentPage(change);
  };

  const tableChangeHandler = (pagination, filters, sorter, extra) => {};

  const handleDelete = async (record) => {
    let data = { ...store.record };
    data.interests = data.interests.filter(
      (interest) => interest.id !== record.id
    );
    await update(data);

    handleCancel();
  };

  const handleCancel = (id) => {
    if (id === "new") {
      setTableData((oldArray) => oldArray.filter((item) => item.id !== id));
    }

    setSelectedRow(null);
  };

  const handleAdd = () => {
    setCurrentPage(1);

    reset();

    const newData = {
      id: "new",
      description: "",
      dateOfBeginning: moment(),
      dateOfFinish: moment(),
      percentage: 0,
    };

    setTableData((oldArray) => [newData, ...oldArray]);
    setSelectedRow(newData);
  };

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputNode: col.inputNode,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        form: form,
      }),
    };
  });

  return (
    <>
      <PageHeader
        title="Faiz Oranları"
        style={{ padding: "0 0 24px 0" }}
        extra={[
          <Button
            key="new"
            disabled={
              AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Koşul Tipleri")
                ? selectedRow
                  ? true
                  : false
                : true
            }
            onClick={() => handleAdd()}
            size={10}
          >
            Faiz Oranı Ekle
          </Button>,
        ]}
      />
      <form onSubmit={handleSubmit(formOnSubmit)}>
        <Table
          rowKey={(row) => row.id}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={tableData}
          columns={mergedColumns}
          onChange={tableChangeHandler}
          size="small"
          loading={store.process === ACTION_TYPES.FETCHING_DATA}
          rowClassName="editable-row"
          pagination={{
            onChange: handleTablePagination,
            current: currentPage,
            defaultPageSize: 12,
          }}
        />
      </form>
    </>
  );
}
